import { lazy, Suspense } from 'react'
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom'
import { QueryClientProvider } from '@tanstack/react-query'
import { AuthProvider, useAuth } from './lib/auth'
import { queryClient } from './lib/queryClient'
import { Shell } from './components/Shell'
import { ErrorBoundary } from './components/ErrorBoundary'
import { Loading, ToastHost } from './components/ui'
import { Login } from './pages/Login'
import { Dashboard } from './pages/Dashboard'

// The map pages carry MapLibre; loading them lazily keeps it out of the
// initial bundle for anyone who only ever opens a report.
const Live = lazy(() => import('./pages/Live').then((m) => ({ default: m.Live })))
const VehicleDetail = lazy(() =>
  import('./pages/VehicleDetail').then((m) => ({ default: m.VehicleDetail })),
)
const Geofences = lazy(() => import('./pages/Geofences').then((m) => ({ default: m.Geofences })))
// Unauthenticated map self-test; see MapCheck.tsx.
const MapCheck = lazy(() => import('./pages/MapCheck').then((m) => ({ default: m.MapCheck })))
// Unauthenticated design-system self-test for both themes; see ThemeCheck.tsx.
const ThemeCheck = lazy(() => import('./pages/ThemeCheck').then((m) => ({ default: m.ThemeCheck })))

import { Events } from './pages/Events'
import { Reports } from './pages/Reports'
import { Vehicles } from './pages/Vehicles'
import { Drivers } from './pages/Drivers'
import { Devices } from './pages/Devices'
import { Tenants } from './pages/Tenants'
import { Users } from './pages/Users'
import { Billing } from './pages/Billing'
import { BrandingPage } from './pages/BrandingPage'
import { Audit } from './pages/Audit'
import { Integrations } from './pages/Integrations'
import { HealthPage } from './pages/Health'
import { Settings } from './pages/Settings'

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <BrowserRouter>
          <ErrorBoundary>
            <Routed />
          </ErrorBoundary>
          <ToastHost />
        </BrowserRouter>
      </AuthProvider>
    </QueryClientProvider>
  )
}

function Routed() {
  const { user, loading } = useAuth()

  if (loading) {
    return (
      <div className="h-full grid place-items-center">
        <Loading label="Starting" />
      </div>
    )
  }

  if (!user) {
    return (
      <Suspense fallback={<div className="h-full grid place-items-center"><Loading /></div>}>
        <Routes>
          <Route path="/__mapcheck" element={<MapCheck />} />
          <Route path="/__themecheck" element={<ThemeCheck />} />
          <Route path="*" element={<Login />} />
        </Routes>
      </Suspense>
    )
  }

  return (
    <Suspense
      fallback={
        <div className="h-full grid place-items-center">
          <Loading />
        </div>
      }
    >
      <Routes>
        <Route path="__mapcheck" element={<MapCheck />} />
        <Route path="__themecheck" element={<ThemeCheck />} />
        <Route element={<Shell />}>
          <Route index element={<Dashboard />} />
          <Route path="live" element={<Live />} />
          <Route path="vehicles" element={<Vehicles />} />
          <Route path="vehicles/:id" element={<VehicleDetail />} />
          <Route path="drivers" element={<Drivers />} />
          <Route path="geofences" element={<Geofences />} />
          <Route path="events" element={<Events />} />
          <Route path="reports" element={<Reports />} />
          <Route path="devices" element={<Devices />} />
          <Route path="tenants" element={<Tenants />} />
          <Route path="tenants/:id" element={<Tenants />} />
          <Route path="users" element={<Users />} />
          <Route path="integrations" element={<Integrations />} />
          <Route path="billing" element={<Billing />} />
          <Route path="branding" element={<BrandingPage />} />
          <Route path="audit" element={<Audit />} />
          <Route path="health" element={<HealthPage />} />
          <Route path="settings" element={<Settings />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    </Suspense>
  )
}
